import React, { useEffect, useRef, useState } from 'react';

interface AnimatedStatProps {
  value: string;
  label: string;
  className?: string;
  numberClassName?: string;
  labelClassName?: string;
  showBar?: boolean;
}

const parseStat = (raw: string): { target: number; suffix: string; decimals: number } => {
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (!match) {
    return { target: 0, suffix: raw, decimals: 0 };
  }
  const num = match[1];
  return {
    target: Number(num),
    suffix: match[2] || '',
    decimals: num.includes('.') ? num.split('.')[1].length : 0,
  };
};

const AnimatedStat: React.FC<AnimatedStatProps> = ({
  value,
  label,
  className = '',
  numberClassName = '',
  labelClassName = '',
  showBar = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [display, setDisplay] = useState(value);
  const [started, setStarted] = useState(false);
  const { target, suffix, decimals } = parseStat(value);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || Number.isNaN(target) || target <= 0) {
      setDisplay(value);
      setStarted(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStarted(true);
        observer.disconnect();

        const duration = 1100;
        const start = performance.now();
        const tick = (now: number) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = target * eased;
          setDisplay(`${current.toFixed(decimals)}${suffix}`);
          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            setDisplay(value);
          }
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [decimals, suffix, target, value]);

  return (
    <div ref={ref} className={`animated-stat ${started ? 'is-started' : ''} ${className}`}>
      <div className={numberClassName}>{display}</div>
      <div className={labelClassName}>{label}</div>
      {showBar && <span className="animated-stat-bar" aria-hidden="true" />}
    </div>
  );
};

export default AnimatedStat;
